/* In-browser skin scoring — TEST BUILD.
 *
 * JS port of the skin-scan OSS heuristics (github.com/DurtyDhiana/skin-scan,
 * src/pipeline/maps/*.py + compose.py) so the scan needs NO backend: the
 * same classical image measures, computed on the captured photo with the
 * MediaPipe landmarks we already have. Each score = mean of the concern map
 * over the face mask, maps min-max normalised over the face, exactly like
 * the repo's score_from_map(). Known approximations vs the Python original:
 * morphology uses square structuring elements (theirs: ellipse), the
 * blemish tophat uses a square window, and pore areas scale with our 512px
 * working size (theirs: 1024). Direction quirk kept: hydration is
 * higher-is-better, everything else is higher-is-worse.
 */
(function () {
  'use strict';

  var WORK = 512; /* max working dimension (repo preprocesses to 1024; 512 keeps it snappy) */

  /* FaceMesh silhouette ring — the standard face outline indices. */
  var SILHOUETTE = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
    397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132,
    93, 234, 127, 162, 21, 54, 103, 67, 109];

  /* ---- small image toolkit (Float32Array planes, w*h) ------------------- */

  function gaussKernel(sigma) {
    var r = Math.max(1, Math.ceil(3 * sigma)), k = new Float32Array(2 * r + 1), s = 0;
    for (var i = -r; i <= r; i++) { k[i + r] = Math.exp(-(i * i) / (2 * sigma * sigma)); s += k[i + r]; }
    for (var j = 0; j < k.length; j++) k[j] /= s;
    return { k: k, r: r };
  }

  function blur(src, w, h, sigma) {
    var g = gaussKernel(sigma), k = g.k, r = g.r;
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h);
    var x, y, i, acc;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        acc = 0;
        for (i = -r; i <= r; i++) {
          var xx = Math.min(w - 1, Math.max(0, x + i));
          acc += src[y * w + xx] * k[i + r];
        }
        tmp[y * w + x] = acc;
      }
    }
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        acc = 0;
        for (i = -r; i <= r; i++) {
          var yy = Math.min(h - 1, Math.max(0, y + i));
          acc += tmp[yy * w + x] * k[i + r];
        }
        out[y * w + x] = acc;
      }
    }
    return out;
  }

  function conv3(src, w, h, m) { /* 3x3 convolution, replicate borders */
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var acc = 0, n = 0;
        for (var dy = -1; dy <= 1; dy++) {
          for (var dx = -1; dx <= 1; dx++) {
            var yy = Math.min(h - 1, Math.max(0, y + dy));
            var xx = Math.min(w - 1, Math.max(0, x + dx));
            acc += src[yy * w + xx] * m[n++];
          }
        }
        out[y * w + x] = acc;
      }
    }
    return out;
  }

  var LAP = [0, 1, 0, 1, -4, 1, 0, 1, 0];
  var SOBX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  var SOBY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

  function gradMag(gray, w, h) {
    var gx = conv3(gray, w, h, SOBX), gy = conv3(gray, w, h, SOBY);
    var out = new Float32Array(w * h);
    for (var i = 0; i < out.length; i++) out[i] = Math.sqrt(gx[i] * gx[i] + gy[i] * gy[i]);
    return out;
  }

  /* binary morphology, square window radius r */
  function erodeBin(src, w, h, r) {
    var out = new Uint8Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var keep = 1;
        for (var dy = -r; dy <= r && keep; dy++) {
          for (var dx = -r; dx <= r; dx++) {
            var yy = y + dy, xx = x + dx;
            if (yy < 0 || yy >= h || xx < 0 || xx >= w || !src[yy * w + xx]) { keep = 0; break; }
          }
        }
        out[y * w + x] = keep;
      }
    }
    return out;
  }
  function dilateBin(src, w, h, r) {
    var out = new Uint8Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var hit = 0;
        for (var dy = -r; dy <= r && !hit; dy++) {
          for (var dx = -r; dx <= r; dx++) {
            var yy = y + dy, xx = x + dx;
            if (yy >= 0 && yy < h && xx >= 0 && xx < w && src[yy * w + xx]) { hit = 1; break; }
          }
        }
        out[y * w + x] = hit;
      }
    }
    return out;
  }
  function openBin(src, w, h, r) { return dilateBin(erodeBin(src, w, h, r), w, h, r); }

  /* grayscale erosion (for the blemish tophat), square radius r */
  function erodeGray(src, w, h, r) {
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var mn = Infinity;
        for (var dy = -r; dy <= r; dy++) {
          for (var dx = -r; dx <= r; dx++) {
            var yy = Math.min(h - 1, Math.max(0, y + dy));
            var xx = Math.min(w - 1, Math.max(0, x + dx));
            var v = src[yy * w + xx];
            if (v < mn) mn = v;
          }
        }
        out[y * w + x] = mn;
      }
    }
    return out;
  }
  function dilateGray(src, w, h, r) {
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var mx = -Infinity;
        for (var dy = -r; dy <= r; dy++) {
          for (var dx = -r; dx <= r; dx++) {
            var yy = Math.min(h - 1, Math.max(0, y + dy));
            var xx = Math.min(w - 1, Math.max(0, x + dx));
            var v = src[yy * w + xx];
            if (v > mx) mx = v;
          }
        }
        out[y * w + x] = mx;
      }
    }
    return out;
  }

  function otsu(vals255) { /* threshold over 0..255 values */
    var hist = new Float64Array(256), n = vals255.length, i;
    for (i = 0; i < n; i++) hist[Math.min(255, Math.max(0, Math.round(vals255[i])))]++;
    var sum = 0; for (i = 0; i < 256; i++) sum += i * hist[i];
    var sumB = 0, wB = 0, best = 0, thr = 0;
    for (i = 0; i < 256; i++) {
      wB += hist[i]; if (!wB) continue;
      var wF = n - wB; if (!wF) break;
      sumB += i * hist[i];
      var mB = sumB / wB, mF = (sum - sumB) / wF;
      var between = wB * wF * (mB - mF) * (mB - mF);
      if (between > best) { best = between; thr = i; }
    }
    return thr;
  }

  function percentile(vals, p) {
    var a = Array.prototype.slice.call(vals).sort(function (x, y) { return x - y; });
    return a[Math.min(a.length - 1, Math.max(0, Math.floor(a.length * p / 100)))];
  }

  function maskedVals(map, mask) {
    var out = [];
    for (var i = 0; i < map.length; i++) if (mask[i]) out.push(map[i]);
    return out;
  }
  function maskedMean(map, mask) {
    var s = 0, n = 0;
    for (var i = 0; i < map.length; i++) if (mask[i]) { s += map[i]; n++; }
    return n ? s / n : 0;
  }
  function maskedMax(map, mask) {
    var m = 0;
    for (var i = 0; i < map.length; i++) if (mask[i] && map[i] > m) m = map[i];
    return m;
  }
  /* min-max normalise over the face then take the mean — score_from_map()
   * applied to the repo's normalised maps. */
  function normalizedMean(map, mask) {
    var mn = Infinity, mx = -Infinity, i;
    for (i = 0; i < map.length; i++) if (mask[i]) { if (map[i] < mn) mn = map[i]; if (map[i] > mx) mx = map[i]; }
    if (!(mx - mn > 1e-6)) return 0;
    var s = 0, n = 0;
    for (i = 0; i < map.length; i++) if (mask[i]) { s += Math.min(1, Math.max(0, (map[i] - mn) / (mx - mn))); n++; }
    return n ? s / n : 0;
  }

  /* connected components with area filter (pores) */
  function componentAreaFilter(bin, w, h, minA, maxA) {
    var out = new Uint8Array(w * h), seen = new Uint8Array(w * h), stack = [];
    for (var i = 0; i < bin.length; i++) {
      if (!bin[i] || seen[i]) continue;
      var comp = [], head = 0;
      stack.length = 0; stack.push(i); seen[i] = 1;
      while (head < stack.length) {
        var p = stack[head++]; comp.push(p);
        var px = p % w, py = (p / w) | 0;
        if (px > 0 && bin[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack.push(p - 1); }
        if (px < w - 1 && bin[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack.push(p + 1); }
        if (py > 0 && bin[p - w] && !seen[p - w]) { seen[p - w] = 1; stack.push(p - w); }
        if (py < h - 1 && bin[p + w] && !seen[p + w]) { seen[p + w] = 1; stack.push(p + w); }
      }
      if (comp.length > minA && comp.length < maxA) {
        for (var c = 0; c < comp.length; c++) out[comp[c]] = 1;
      }
    }
    return out;
  }

  /* uniform LBP, P=8 R=1 (repo's texture co-feature) */
  function lbpUniform(gray, w, h) {
    var out = new Float32Array(w * h);
    var off = [-w - 1, -w, -w + 1, 1, w + 1, w, w - 1, -1];
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        var i = y * w + x, c = gray[i], code = [], ones = 0, trans = 0, b;
        for (b = 0; b < 8; b++) { var v = gray[i + off[b]] >= c ? 1 : 0; code.push(v); ones += v; }
        for (b = 0; b < 8; b++) if (code[b] !== code[(b + 1) % 8]) trans++;
        out[i] = trans <= 2 ? ones : 9; /* uniform -> count of ones, else P+1 */
      }
    }
    return out;
  }

  /* ---- colour planes (OpenCV 8U scaling) --------------------------------- */

  function planes(imgData, w, h) {
    var d = imgData.data, n = w * h;
    var gray = new Float32Array(n), S = new Float32Array(n), V = new Float32Array(n);
    var L = new Float32Array(n), A = new Float32Array(n), B = new Float32Array(n);
    for (var i = 0; i < n; i++) {
      var r = d[i * 4], g = d[i * 4 + 1], b = d[i * 4 + 2];
      gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      V[i] = mx;
      S[i] = mx ? 255 * (mx - mn) / mx : 0;
      /* sRGB -> LAB, OpenCV 8U scaling (L*255/100, a+128, b+128) */
      var rl = srgb2lin(r / 255), gl = srgb2lin(g / 255), bl = srgb2lin(b / 255);
      var X = (0.4124 * rl + 0.3576 * gl + 0.1805 * bl) / 0.950456;
      var Y = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
      var Z = (0.0193 * rl + 0.1192 * gl + 0.9505 * bl) / 1.088754;
      var fx = labF(X), fy = labF(Y), fz = labF(Z);
      L[i] = (116 * fy - 16) * 255 / 100;
      A[i] = 500 * (fx - fy) + 128;
      B[i] = 200 * (fy - fz) + 128;
    }
    return { gray: gray, S: S, V: V, L: L, A: A, B: B };
  }
  function srgb2lin(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function labF(t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; }

  /* ---- the seven concern scores ------------------------------------------ */

  function scoreRedness(P, mask) {
    var vals = maskedVals(P.A, mask);
    var mu = 0, i; for (i = 0; i < vals.length; i++) mu += vals[i]; mu /= vals.length || 1;
    var sd = 0; for (i = 0; i < vals.length; i++) sd += (vals[i] - mu) * (vals[i] - mu);
    sd = Math.sqrt(sd / (vals.length || 1)) + 1e-6;
    var z = new Float32Array(P.A.length);
    for (i = 0; i < z.length; i++) z[i] = (P.A[i] - mu) / sd;
    return normalizedMean(z, mask);
  }

  function scoreOiliness(P, mask, w, h) {
    var gm = gradMag(P.gray, w, h);
    var spec = new Uint8Array(w * h);
    for (var i = 0; i < spec.length; i++) spec[i] = (P.V[i] > 200 && P.S[i] < 80 && gm[i] < 50) ? 1 : 0;
    spec = dilateBin(openBin(spec, w, h, 2), w, h, 2);
    return maskedMean(spec, mask);
  }

  function scoreTexture(P, mask, w, h) {
    var lap = conv3(P.gray, w, h, LAP), i;
    var lap2 = new Float32Array(lap.length);
    for (i = 0; i < lap.length; i++) lap2[i] = lap[i] * lap[i];
    var variance = blur(lap2, w, h, 1.7);
    var lbp = blur(lbpUniform(P.gray, w, h), w, h, 1.7);
    var vmax = maskedMax(variance, mask) + 1e-6, lmax = maskedMax(lbp, mask) + 1e-6;
    var tex = new Float32Array(lap.length);
    for (i = 0; i < tex.length; i++) tex[i] = 0.6 * variance[i] / vmax + 0.4 * lbp[i] / lmax;
    return normalizedMean(tex, mask);
  }

  function scorePores(P, mask, w, h) {
    var b1 = blur(P.gray, w, h, 0.5), b2 = blur(P.gray, w, h, 2.0), i;
    var hp = new Float32Array(w * h), mn = Infinity, mx = -Infinity;
    for (i = 0; i < hp.length; i++) {
      hp[i] = Math.max(0, b1[i] - b2[i]); /* cv2.subtract saturates at 0 */
      if (hp[i] < mn) mn = hp[i]; if (hp[i] > mx) mx = hp[i];
    }
    var range = (mx - mn) || 1;
    for (i = 0; i < hp.length; i++) hp[i] = (hp[i] - mn) / range * 255;
    var thr = otsu(hp);
    var bin = new Uint8Array(w * h);
    for (i = 0; i < bin.length; i++) bin[i] = hp[i] > thr ? 0 : 1; /* inverted: pores are dark */
    bin = openBin(bin, w, h, 1);
    var scale = (Math.max(w, h) / 1024) * (Math.max(w, h) / 1024); /* repo areas assume 1024px */
    var poreMap = componentAreaFilter(bin, w, h, 4 * scale, 80 * scale);
    var density = blur(Float32Array.from(poreMap, function (v) { return v * 255; }), w, h, 5);
    return normalizedMean(density, mask);
  }

  function scoreBlemishes(P, mask, w, h) {
    var i, n = w * h;
    var aP70 = percentile(maskedVals(P.A, mask), 70);
    var gBlur = blur(P.gray, w, h, 2.6);
    var lBlur = blur(P.L, w, h, 2.6);
    var cvar = new Float32Array(n);
    for (i = 0; i < n; i++) cvar[i] = Math.abs(P.L[i] - lBlur[i]);
    var vP60 = percentile(maskedVals(cvar, mask), 60);
    /* tophat = gray - open(gray), then otsu */
    var opened = dilateGray(erodeGray(P.gray, w, h, 4), w, h, 4);
    var tophat = new Float32Array(n);
    for (i = 0; i < n; i++) tophat[i] = Math.max(0, P.gray[i] - opened[i]);
    var thThr = otsu(tophat);
    var bin = new Uint8Array(n);
    for (i = 0; i < n; i++) {
      var votes = (P.A[i] > aP70 ? 1 : 0)
        + (gBlur[i] - P.gray[i] > 5 ? 1 : 0)
        + (cvar[i] > vP60 ? 1 : 0)
        + (tophat[i] > thThr ? 1 : 0);
      bin[i] = votes >= 2 ? 1 : 0;
    }
    bin = openBin(bin, w, h, 1);
    var density = blur(Float32Array.from(bin, function (v) { return v * 255; }), w, h, 3);
    var dmax = maskedMax(density, mask);
    if (dmax < 1e-6) return 0;
    var out = new Float32Array(n);
    for (i = 0; i < n; i++) out[i] = Math.min(1, density[i] / dmax);
    return maskedMean(out, mask);
  }

  function scoreHydration(P, mask, w, h) {
    var i, n = w * h;
    var lap = conv3(P.gray, w, h, LAP);
    var absLap = new Float32Array(n);
    for (i = 0; i < n; i++) absLap[i] = Math.abs(lap[i]);
    var rough = blur(absLap, w, h, 1.7);
    var smooth = new Float32Array(n);
    for (i = 0; i < n; i++) smooth[i] = 1 / (rough[i] + 1e-6);
    var smax = maskedMax(smooth, mask) + 1e-6;
    var vBlur = blur(P.V, w, h, 2.6);
    var dev2 = new Float32Array(n);
    for (i = 0; i < n; i++) { var dv = P.V[i] - vBlur[i]; dev2[i] = dv * dv; }
    var vStd = blur(dev2, w, h, 2.6);
    var even = new Float32Array(n);
    for (i = 0; i < n; i++) even[i] = 1 / (Math.sqrt(vStd[i]) + 1e-6);
    var emax = maskedMax(even, mask) + 1e-6;
    var gm = gradMag(P.gray, w, h);
    var gs = new Float32Array(n);
    for (i = 0; i < n; i++) gs[i] = 1 / (gm[i] + 1e-6);
    var gmax = maskedMax(gs, mask) + 1e-6;
    var hyd = new Float32Array(n);
    for (i = 0; i < n; i++) hyd[i] = 0.4 * smooth[i] / smax + 0.3 * even[i] / emax + 0.3 * gs[i] / gmax;
    return normalizedMean(hyd, mask); /* higher = more hydrated, repo convention */
  }

  function scorePigment(P, mask) {
    var vals = maskedVals(P.L, mask), i;
    var mu = 0; for (i = 0; i < vals.length; i++) mu += vals[i]; mu /= vals.length || 1;
    var sd = 0; for (i = 0; i < vals.length; i++) sd += (vals[i] - mu) * (vals[i] - mu);
    sd = Math.sqrt(sd / (vals.length || 1)) + 1e-6;
    var pig = new Float32Array(P.L.length);
    for (i = 0; i < pig.length; i++) {
      var dark = Math.max(0, (mu - P.L[i]) / sd);
      var by = Math.min(1, Math.max(0, (P.B[i] - 128) / 128));
      var br = Math.min(1, Math.max(0, (P.A[i] - 128) / 128 * 0.5));
      pig[i] = dark * 0.5 + by * 0.3 + br * 0.2;
    }
    return normalizedMean(pig, mask);
  }

  /* ---- entry point --------------------------------------------------------- */

  window.__skinScanScores = function (photoCanvas, landmarks) {
    var scale = Math.min(1, WORK / Math.max(photoCanvas.width, photoCanvas.height));
    var w = Math.max(1, Math.round(photoCanvas.width * scale));
    var h = Math.max(1, Math.round(photoCanvas.height * scale));

    var work = document.createElement('canvas');
    work.width = w; work.height = h;
    var ctx = work.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(photoCanvas, 0, 0, w, h);

    /* face mask from the landmark silhouette */
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.restore();
    var maskCanvas = document.createElement('canvas');
    maskCanvas.width = w; maskCanvas.height = h;
    var mctx = maskCanvas.getContext('2d', { willReadFrequently: true });
    mctx.fillStyle = '#fff';
    mctx.beginPath();
    for (var s = 0; s < SILHOUETTE.length; s++) {
      var p = landmarks[SILHOUETTE[s]];
      if (s === 0) mctx.moveTo(p.x * w, p.y * h);
      else mctx.lineTo(p.x * w, p.y * h);
    }
    mctx.closePath();
    mctx.fill();
    var maskData = mctx.getImageData(0, 0, w, h).data;
    var mask = new Uint8Array(w * h);
    for (var i = 0; i < mask.length; i++) mask[i] = maskData[i * 4] > 127 ? 1 : 0;

    var P = planes(ctx.getImageData(0, 0, w, h), w, h);

    return {
      redness: scoreRedness(P, mask),
      oiliness: scoreOiliness(P, mask, w, h),
      texture: scoreTexture(P, mask, w, h),
      pores: scorePores(P, mask, w, h),
      blemishes: scoreBlemishes(P, mask, w, h),
      hydration: scoreHydration(P, mask, w, h),
      pigment: scorePigment(P, mask)
    };
  };
})();
